import React from 'react'
import {
  AdminMedicalInstitutionListItem,
  ApplicationEntryFormProps,
} from 'types/med'
import { makeStyles } from 'tss-react/mui'
import {
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Link,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import GetAppIcon from '@mui/icons-material/GetApp'
import type { Theme } from '@mui/material/styles'

import { Layout } from 'ui/layout/Layout'
import { AppBarIhAdmin } from 'ui/partials/AppBar'
import {
  csvFileName,
  downLoadCsvText,
  arrayToCsvText,
} from 'ui/utils/downloadCsvText'

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    display: 'flex',
    height: '100vh',
  },

  tableWrapper: {
    display: 'flex',
    height: 'calc(100vh - 65px)',
  },

  td: {
    flex: 0,
    wordBreak: 'keep-all',
    padding: theme.spacing(0.5, 1),
  },

  noMargin: {
    padding: theme.spacing(0),
    margin: theme.spacing(0),
  },

  divider: {
    borderLeft: `1px solid ${theme.palette.divider}`,
  },

  tr: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
}))

export type MedicalInstitutionListProps = {
  institutions: AdminMedicalInstitutionListItem[]
  onClickEdit: (id: ApplicationEntryFormProps['id']) => void
  onResendMail: (id: ApplicationEntryFormProps['id']) => Promise<void>
}

const headItems = [
  'ID',
  '医療機関名',
  '電話番号',
  '郵便番号',
  '都道府県',
  '市区町村',
  '町域',
  '丁目字通り名',
  '建物名',
  'Stripe',
  'Teams',
  'Zoom',
  '申込年月日',
  '申込フォームURL',
  '医療機関別URL',
  '利用中',
  'YaDoc',
  '紹介代理店',
  //プラン
  'プランカテゴリ',
  'プラン',
  'キャンペーン',
  //管理者
  'ID',
  '姓',
  '名',
  'せい',
  'めい',
  'email',
  //代理店
  '代理店',
  '支店',
  '担当者',
  'メールアドレス',
  '電話番号',
  '備考',
  'メール',
]

const dividerIndexes = [0, 9, 12, 14, 15, 16, 17, 20, 23]

const MedicalInstitutionList: React.FC<MedicalInstitutionListProps> = ({
  institutions,
  onClickEdit,
  onResendMail,
}: MedicalInstitutionListProps) => {
  const { classes, cx } = useStyles()

  const rowData: (
    med: AdminMedicalInstitutionListItem
  ) => (string | number | null | undefined)[] = (med) => {
    return [
      med.id,
      med.name,
      med.phone,
      med.postal_code,
      med.prefecture,
      med.city,
      med.town,
      med.street,
      med.building,
      med.is_connected_stripe ? '◯' : '-',
      med?.video_call_methods?.find(({ name }) => name === 'video_teams')
        ?.has_connection
        ? '◯'
        : '-',
      med?.video_call_methods?.find(({ name }) => name === 'video_zoom')
        ?.has_connection
        ? '◯'
        : '-',
      med.application_date,
      med.form_url,
      med.online_eligibility_verification_url,
      med.is_active ? '◯' : '-',
      med.is_applied_yadoc ? '◯' : '-',
      med.introduced_partner || '-',
      // プラン
      med.plan_category,
      med.plan,
      med.campaign_code || '-',
      // 管理者
      med.admin_user?.id,
      med.admin_user?.last_name,
      med.admin_user?.first_name,
      med.admin_user?.last_name_kana,
      med.admin_user?.first_name_kana,
      med.admin_user?.email,
      //代理店
      med.partner?.name,
      med.partner?.branch_name,
      med.partner?.contact_person_name,
      med.partner?.email,
      med.partner?.phone,
      med.partner?.remarks,
    ]
  }

  const downloadCsvHandler = () => {
    downLoadCsvText(
      arrayToCsvText([headItems, ...institutions.map((med) => rowData(med))]),
      csvFileName()
    )
  }

  const resendMailHandler = (med: ApplicationEntryFormProps) => {
    if (window.confirm(`${med.name}に申込みメールを再送信しますか？ `)) {
      onResendMail(med.id)
        .then(() => alert('送信しました'))
        .catch(() => alert('送信に失敗しました'))
    }
  }

  const Td: React.FC<{
    className?: string
    children?: any
  }> = ({ className, children }) => (
    <TableCell size={'small'} className={cx(classes.td, className)}>
      {children}
    </TableCell>
  )

  const tr = (index: number, med: ApplicationEntryFormProps) => {
    return (
      <TableRow key={`${med.name}-${index}`} className={classes.tr}>
        <Td>
          <IconButton
            color={'primary'}
            size={'small'}
            onClick={() => {
              onClickEdit(med.id)
            }}
          >
            <EditIcon>編集</EditIcon>
          </IconButton>
        </Td>
        {rowData(med).map((value, rowIndex) => (
          <Td
            key={`ih-admin-row-${index}-${rowIndex}`}
            className={cx(dividerIndexes.includes(rowIndex) && classes.divider)}
          >
            {(rowIndex === 13 || rowIndex === 14) &&
            typeof value === 'string' ? (
              <Link target="_blank" rel="noopener noreferrer" href={value}>
                {value}
              </Link>
            ) : (
              value ?? ''
            )}
          </Td>
        ))}
        <Td className={classes.noMargin}>
          <Button
            className={classes.noMargin}
            color={'primary'}
            onClick={() => resendMailHandler(med)}
          >
            再送信
          </Button>
        </Td>
      </TableRow>
    )
  }

  return (
    <Layout
      header={
        <AppBarIhAdmin
          util={
            <Button
              onClick={downloadCsvHandler}
              startIcon={<GetAppIcon />}
              color={'primary'}
            >
              CSV
            </Button>
          }
        >
          YaDoc Quick 管理
        </AppBarIhAdmin>
      }
    >
      <TableContainer component={Paper} className={classes.tableWrapper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <Td />
              {headItems.map((item, index) => (
                <Td
                  key={`ih-admin-head-${index}`}
                  className={cx(
                    dividerIndexes.includes(index) && classes.divider
                  )}
                >
                  {item}
                </Td>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {institutions.map((med, index) => tr(index, med))}
          </TableBody>
        </Table>
      </TableContainer>
    </Layout>
  )
}

export default MedicalInstitutionList
